import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  chatAPI,
  notificationsAPI,
  SystemNotification,
} from '../services/api';
import {
  disconnectChatSocket,
  ensureChatSocketConnection,
} from '../services/chatSocket';

const POLL_INTERVAL_MS = 30000;

export const useNotificationCenter = () => {
  const { user, token } = useAuth();
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [systemUnreadCount, setSystemUnreadCount] = useState(0);
  const [systemNotifications, setSystemNotifications] = useState<SystemNotification[]>([]);
  const [loading, setLoading] = useState(false);

  const refreshChatUnread = useCallback(async () => {
    if (!user) {
      setChatUnreadCount(0);
      return;
    }

    try {
      const data = await chatAPI.getUnreadCount();
      setChatUnreadCount(data.totalUnreadCount);
    } catch {
      // Silent background refresh.
    }
  }, [user]);

  const refreshSystemNotifications = useCallback(async () => {
    if (!user) {
      setSystemNotifications([]);
      setSystemUnreadCount(0);
      return;
    }

    try {
      const data = await notificationsAPI.getSummary({ limit: 12 });
      setSystemNotifications(data.notifications);
      setSystemUnreadCount(data.unreadCount);
    } catch {
      // Silent background refresh.
    }
  }, [user]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        refreshChatUnread(),
        refreshSystemNotifications(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [refreshChatUnread, refreshSystemNotifications]);

  const markNotificationRead = useCallback(async (id: number) => {
    const updatedNotification = await notificationsAPI.markRead(id);
    const wasUnread = systemNotifications.some((notification) => (
      notification.id === id && !notification.readAt
    ));

    setSystemNotifications((current) => current.map((notification) => (
      notification.id === id ? updatedNotification : notification
    )));

    if (wasUnread && updatedNotification.readAt) {
      setSystemUnreadCount((current) => Math.max(current - 1, 0));
    }

    return updatedNotification;
  }, [systemNotifications]);

  const markAllSystemRead = useCallback(async () => {
    await notificationsAPI.markAllRead();
    const readAt = new Date().toISOString();

    setSystemNotifications((current) => current.map((notification) => ({
      ...notification,
      readAt: notification.readAt ?? readAt,
    })));
    setSystemUnreadCount(0);
  }, []);

  useEffect(() => {
    void refresh();
    const intervalId = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refresh]);

  useEffect(() => {
    const handleFocus = () => {
      void refresh();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refresh]);

  useEffect(() => {
    if (!token) {
      disconnectChatSocket();
      setChatUnreadCount(0);
      return;
    }

    const socket = ensureChatSocketConnection(token);
    if (!socket) {
      return;
    }

    const refreshUnreadCount = () => {
      void refreshChatUnread();
    };

    socket.on('connect', refreshUnreadCount);
    socket.on('chat:connected', refreshUnreadCount);
    socket.on('chat:conversation:updated', refreshUnreadCount);
    socket.on('chat:unread:changed', refreshUnreadCount);

    return () => {
      socket.off('connect', refreshUnreadCount);
      socket.off('chat:connected', refreshUnreadCount);
      socket.off('chat:conversation:updated', refreshUnreadCount);
      socket.off('chat:unread:changed', refreshUnreadCount);
    };
  }, [refreshChatUnread, token]);

  const totalUnreadCount = useMemo(
    () => chatUnreadCount + systemUnreadCount,
    [chatUnreadCount, systemUnreadCount],
  );

  return {
    chatUnreadCount,
    systemUnreadCount,
    systemNotifications,
    totalUnreadCount,
    loading,
    refresh,
    markNotificationRead,
    markAllSystemRead,
  };
};
